import { createContext, type ComponentChildren } from 'preact';
import { useContext } from 'preact/hooks';
import { Link, useRoute } from '../lib/router';
import { useNotice } from '../shell/notice';
import { useRemembered } from '../lib/remember';
import { Fold, FoldAll } from './fold';
import { useState } from 'preact/hooks';
import { Chip, Cover, InterestDots, READ_TYPES, ToggleChips } from '../ui/bits';
import { Popover } from '../ui/Popover';
import { MenuList, PillMenu } from '../ui/PillMenu';
import type { BookEdits } from './actions';
import { AddEntry, FinishSheet, MovePlaceSheet, PauseReason, ProgressSlider, StartSheet, Stars, StopSheet, type StopChoice } from './reading';
import { AddCopySheet, LoanSteps } from './library';
import { RecentChanges, useRecentChanges } from './recent';
import { PasteKindleSheet } from './kindleSheet';
import { CopyTools } from '../mybooks/copyTools';
import { NameLink } from '../ui/NameLink';
import { honourLine } from '../discover/lists';
import { trailState } from '../shell/sections';
import { CopyEdits, EditAbout, EditingCtx, EntryEdits, EventEdits, FansOfEdit, GenresTagsEdit, LoanEdits, PastReadSheet, ReadEdits, RemoveBook, useEditing } from './edit';
import { useFlag } from '../shell/hooks';
import { INTEREST_HINTS, NOT_FOR_ME_CHIPS, goodreadsLine, interestHint, moveChoices, notForMeKindOf, type NotForMeKind, type SetField } from './changes';
import {
  ADDED_HOW,
  bookLabel,
  copiesInOrder,
  copiesInUse,
  copyFacts,
  copyName,
  coverOf,
  currentLoan,
  currentRead,
  dayText,
  daysBetween,
  editionFacts,
  editionOf,
  fuzzyText,
  amazonLink,
  catalogSearch,
  goodreadsLink,
  historyParts,
  loanBadge,
  renewFrom,
  latestProgress,
  libbySearch,
  libraryHistory,
  loanLine,
  openRead,
  openTargets,
  positionLine,
  positionOn,
  ratingLine,
  readState,
  readWhen,
  readingAs,
  readsInOrder,
  statusName,
  statusPillRows,
  summarySource,
  timeText,
  todayIn,
} from './model';
import type { BookData, Copy, Note, Quote, Read, Status } from './types';
import './book.css';

// The book page (Phase 6.1; boards 4 and 5, with the decisions doc winning where they differ).
// Part 1 shows the book; Part 2 adds Move ▾, the marks (♥ ↻ ↺), interest, read type and your
// own words set in place, each with Undo; Parts 3–6 add reading, copies, edit mode and Paste
// from Kindle. A button whose feature flag is off is hidden (consistency pass, R11).

const isPhoneDevice = () =>
  typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

/** Which Part 3 sheet is open (one at a time, drawn once at the page's root). */
type OpenSheet =
  | { kind: 'add_copy' }
  | { kind: 'past'; move?: boolean }
  | { kind: 'didnt_finish'; copy: string }
  | { kind: 'start' } | { kind: 'stop'; to?: StopChoice } | { kind: 'finish' } | { kind: 'place'; to: 'up_next' | 'future_reads' } | null;
const SheetCtx = createContext<(s: OpenSheet) => void>(() => {});
const useSheet = () => useContext(SheetCtx);


function Rows({ rows }: { rows: [string, ComponentChildren][] }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== false);
  if (!shown.length) return null;
  return (
    <dl class="bk-rows">
      {shown.map(([k, v]) => (
        <div key={k} class="bk-row">
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A small-capitals label (board 4's 11 px headings) with optional normal-case words after it. */
function Label({ children, aside }: { children: ComponentChildren; aside?: ComponentChildren }) {
  return (
    <span class="bk-label">
      {children}
      {aside ? <span class="bk-label-aside"> {aside}</span> : null}
    </span>
  );
}

// ---------- the parts ----------

function OpenIn({ book }: { book: BookData }) {
  const targets = openTargets(book, isPhoneDevice());
  if (!targets.length) return null;
  // Which copy is in each button's tooltip; the loan's details are on its card (Kristi, Oct 26).
  // Book actions that leave the app: btn-book, full width, with ↗ (R5).
  return (
    <div class="bk-open" role="group" aria-label="Open this book">
      {targets.map((t) => (
        <a key={t.url} class="btn-book" href={t.url} target="_blank" rel="noopener noreferrer" title={t.sub}>
          {t.label}
        </a>
      ))}
    </div>
  );
}

function Progress({ book, edits }: { book: BookData; edits?: BookEdits }) {
  const read = openRead(book);
  const openSheet = useSheet();
  // No open read: a quiet signpost where the box would be (Kristi, Oct 30), not on Reference.
  if (!read) return book.status === 'reference' ? null : <p class="bk-progress-none">Start reading to track progress.</p>;
  const pct = latestProgress(read);
  const pos = positionLine(book, read, pct);
  return (
    <div class="bk-progress" aria-label="Progress">
      <div class="bk-progress-top">
        <Label>Progress</Label>
        <span class="bk-pct">{pct === null ? 'Not started' : `${Math.round(pct)}%`}</span>
      </div>
      <span class="bk-bar" role="progressbar" aria-valuenow={pct ?? 0} aria-valuemin={0} aria-valuemax={100} aria-label="Progress">
        <span style={{ width: `${Math.max(0, Math.min(100, pct ?? 0))}%` }} />
      </span>
      {pos ? <span class="bk-progress-pos">{pos}</span> : null}
      {edits ? (
        <ProgressSlider key={`${read.id}-${pct}`} book={book} read={read} edits={edits} onFinish={() => openSheet({ kind: 'finish' })} />
      ) : (
        <span class="bk-progress-hint">One progress for the book; a %, a page or the Audible time.</span>
      )}
    </div>
  );
}

function SideLinks({ book }: { book: BookData }) {
  const gr = goodreadsLink(book.work.goodreads_id);
  const libby = libbySearch(book);
  const catalog = catalogSearch(book);
  // Goodreads, Amazon, Libby, Library catalog, as in today's app (Kristi, Oct 30): small,
  // optional links that leave the app, so btn-link with ↗ (R5, Kristi Nov 1).
  return (
    <div class="bk-links">
      {gr ? (
        <a class="btn-link" href={gr} target="_blank" rel="noopener noreferrer">
          Goodreads ↗
        </a>
      ) : null}
      <a class="btn-link" href={amazonLink(book)} target="_blank" rel="noopener noreferrer">
        Amazon ↗
      </a>
      {libby ? (
        <a class="btn-link" href={libby} target="_blank" rel="noopener noreferrer">
          Libby ↗
        </a>
      ) : null}
      {catalog ? (
        <a class="btn-link" href={catalog} target="_blank" rel="noopener noreferrer" title="Search your library's catalog">
          Library catalog ↗
        </a>
      ) : null}
    </div>
  );
}

/**
 * The shelf pill opens Move ▾ (board 4): an outlined teal book action, "Up Next ▾", 32 / 44 px
 * (R4, Kristi Nov 1). Its menu marks the current shelf with ✓; Not for me… asks why.
 */
function MoveButton({ book, edits }: { book: BookData; edits: BookEdits }) {
  const notice = useNotice();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const openSheet = useSheet();
  const editOn = useFlag('book_page.edit');
  const choices = moveChoices(book, { pastRead: Boolean(editOn) });
  const pick = (label: string) => {
    setOpen(false);
    const c = choices.find((x) => x.label === label);
    if (!c || c.here) return;
    if (c.opens === 'start') openSheet({ kind: 'start' });
    else if (c.opens === 'finish') openSheet({ kind: 'finish' });
    else if (c.opens === 'past') openSheet({ kind: 'past', move: true });
    else if (c.opens === 'stop') openSheet({ kind: 'stop', to: c.stopTo });
    else if (!c.to) notice({ message: `${c.label.replace('…', '')}: ${c.note}.`, done: false });
    else if (c.to === 'not_for_me') setAsking(true);
    else if (c.to === 'up_next' || c.to === 'future_reads') openSheet({ kind: 'place', to: c.to });
    else void edits.move(c.to);
  };
  return (
    <span class="pop-host">
      <button
        type="button"
        class="btn-book bk-shelf-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${statusName(book.status)} ▾`}
        title={`Move ${book.work.title}`}
        onClick={() => setOpen(!open)}
      >
        {statusName(book.status)} <span aria-hidden="true">▾</span>
      </button>
      <Popover open={open} onClose={() => setOpen(false)} label="Move to" sheetTitle={`Move ${book.work.title}`}>
        <div class="bk-move">
          <MenuList
            choices={choices.map((c, i) => ({ value: c.label, label: c.label, note: c.here ? undefined : c.note || undefined, heading: i === 0 ? 'Move to' : undefined }))}
            current={choices.find((c) => c.here)?.label ?? null}
            onPick={pick}
          />
        </div>
      </Popover>
      {asking ? <NotForMeSheet book={book} onClose={() => setAsking(false)} edits={edits} /> : null}
    </span>
  );
}

/** Not for me…: which kind (worked out, with change), quick reasons and your own words (never shared). */
export function NotForMeSheet({ book, edits, onClose }: { book: BookData; edits: BookEdits; onClose: () => void }) {
  const guessed = notForMeKindOf(book);
  const [kind, setKind] = useState<NotForMeKind>(guessed);
  const [chips, setChips] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const ok = await edits.move('not_for_me', { kind, chips, reason });
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <>
      <button type="button" class="sheet-scrim" aria-label="Cancel" tabIndex={-1} onClick={onClose} />
      <div class="bk-dialog" role="dialog" aria-modal="true" aria-label={`Not for me: ${book.work.title}`} onKeyDown={(e) => e.key === 'Escape' && onClose()}>
        <h3 class="bk-dialog-title">Move {book.work.title} to Not for me?</h3>
        <p class="bk-dialog-sub">You can bring it back any time; its History keeps the record.</p>
        <div class="bk-dialog-kind">
          Counts as <strong>{kind === 'abandoned' ? 'Abandoned' : 'Passed on'}</strong>
          <span class="bk-quiet">
            {kind === guessed ? (guessed === 'abandoned' ? ' · you stopped a read' : ' · you didn’t start it') : ' · changed by you'}
          </span>
          <button type="button" class="btn-link" onClick={() => setKind(kind === 'abandoned' ? 'passed_on' : 'abandoned')}>
            Change
          </button>
        </div>
        <ToggleChips class="bk-dialog-chips" label="Reasons" choices={NOT_FOR_ME_CHIPS} on={chips} onChange={setChips} small={false} />
        <label class="bk-field">
          <span class="bk-label">Why, in your words</span>
          <input type="text" value={reason} maxLength={2000} placeholder="Optional · only you see this" onInput={(e) => setReason((e.target as HTMLInputElement).value)} />
        </label>
        {book.tryAgainAt ? <p class="bk-dialog-sub">↺ Try again comes off.</p> : null}
        <div class="bk-buttons">
          <button type="button" class="btn-main" disabled={busy} onClick={() => void save()} autoFocus>
            Move to Not for me
          </button>
          <button type="button" class="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}

function Head({ book, today, edits }: { book: BookData; today: string; edits?: BookEdits }) {
  const series = book.series[0];
  return (
    <div class="bk-head">
      <h2 class="book-title">{book.work.title}</h2>
      {book.work.subtitle ? <p class="bk-subtitle">{book.work.subtitle}</p> : null}
      {/* Each author and the series link to their pages (Discover, Part 2; Kristi Nov 3). */}
      {book.authors.length ? (
        <p class="book-author">
          {(book.authorLinks?.length ? book.authorLinks : book.authors.map((name) => ({ id: '', name }))).map((a, i) => (
            <>
              {i ? ', ' : ''}
              <NameLink kind="author" id={a.id} here={book.work.title}>
                {a.name}
              </NameLink>
            </>
          ))}
        </p>
      ) : null}
      {series ? (
        <p class="bk-series">
          <NameLink kind="series" id={series.id} here={book.work.title}>
            {series.name}
          </NameLink>
          {series.label ? ` · ${series.label}` : series.position !== null ? ` · Book ${series.position}` : ''}
        </p>
      ) : null}
      <StatusPills book={book} today={today} edits={edits} />
    </div>
  );
}

/** The pills under the title in rows: library · copies and owned · literary genre, shelf and marks (Kristi, Oct 28). */
function StatusPills({ book, today, edits }: { book: BookData; today: string; edits?: BookEdits }) {
  const r = statusPillRows(book, today);
  const pill = (p: string) => (
    <span key={p} class={`bk-pill ${pillTone(p)}`}>
      {p}
    </span>
  );
  return (
    <div class="bk-pill-rows">
      {r.library.length ? <div class="bk-pills">{r.library.map(pill)}</div> : null}
      {r.copies.length || r.notOwned ? (
        <div class="bk-pills">
          {r.copies.map(pill)}
          {r.notOwned ? <span class="bk-pill notowned">Not owned yet</span> : null}
        </div>
      ) : null}
      <div class="bk-pills">
        {r.literary ? pill(r.literary) : null}
        {edits ? (
          <MoveButton book={book} edits={edits} />
        ) : (
          <span class="bk-pill status">{r.shelf}</span>
        )}
        {r.marks.map(pill)}
      </div>
    </div>
  );
}

/** Pills under the title (board 4): the shelf filled, loans and marks on the teal tint, the rest light grey. */
const pillTone = (p: string) => (/^(Library |♥|Paused|↺|↻)/.test(p) ? 'teal' : 'grey');

/** Pause and Stop reading… while a book is on Currently Reading (Part 3 makes them work). */
/**
 * Part 3: Pause / Resume · Stop reading… · Mark finished… while a book is being read; Start
 * reading (or Start again… / Start a re-read…) when it isn't. The Stop reading sheet is behind
 * book_page.stop_reading (Part 3's flag).
 */
/** ♥ Favorite on its own line under the reading actions, at every width (Kristi, Oct 27). */
function FavoriteButton({ book, edits }: { book: BookData; edits: BookEdits }) {
  const on = Boolean(book.favoriteAt);
  return (
    <div class="bk-fav-row">
      <button type="button" class={`btn-book bk-fav${on ? ' on' : ''}`} aria-pressed={on} title={on ? 'A book you love · tap to take it off' : 'A book you love'} onClick={() => void edits.mark('favorite', !on)}>
        {on ? '♥ Favorite' : '♡ Favorite'}
      </button>
    </div>
  );
}

function ReadingActions({ book, edits }: { book: BookData; edits?: BookEdits }) {
  if (!edits) return null;
  return (
    <>
      <ReadingButtons book={book} edits={edits} />
      <FavoriteButton book={book} edits={edits} />
    </>
  );
}

function ReadingButtons({ book, edits }: { book: BookData; edits: BookEdits }) {
  const read = openRead(book);
  const openSheet = useSheet();
  const stopOn = useFlag('book_page.stop_reading');
  if (!read) {
    const rereading = book.reads.some((r) => r.outcome === 'finished');
    const again = !rereading && book.reads.some((r) => r.outcome === 'stopped');
    return (
      <div class="bk-acts">
        {/* Start reading is Main; Start again… and Start a re-read… are book actions (R5, Kristi Nov 1). */}
        <button type="button" class={rereading || again ? 'btn-book' : 'btn-main'} onClick={() => openSheet({ kind: 'start' })}>
          {rereading ? 'Start a re-read…' : again ? 'Start again…' : 'Start reading'}
        </button>
      </div>
    );
  }
  const pause = read.pauses.find((p) => p.paused_on && !p.resumed_on);
  return (
    <div class="bk-acts">
      <button
        type="button"
        class="btn-book"
        onClick={() => void edits.act(pause ? 'read_resume' : 'read_pause', {}, pause ? `Resumed ${book.work.title}` : `Paused ${book.work.title}`)}
      >
        {pause ? 'Resume reading' : 'Pause'}
      </button>
      {stopOn ? (
        <button type="button" class="btn-book" aria-haspopup="dialog" onClick={() => openSheet({ kind: 'stop' })}>
          Stop reading…
        </button>
      ) : null}
      <button type="button" class="btn-book" aria-haspopup="dialog" onClick={() => openSheet({ kind: 'finish' })}>
        Mark finished…
      </button>
      {pause ? (
        <span class="bk-acts-hint">
          Paused{pause.paused_on ? ` ${dayText(pause.paused_on)}` : ''} · <PauseReason book={book} edits={edits} existing={{ chips: pause.chips, reason: pause.reason }} />
        </span>
      ) : (
        <span class="bk-acts-hint">Pause if you’re still reading it; Stop reading if you’re setting it aside.</span>
      )}
    </div>
  );
}

function Plan({ book, edits }: { book: BookData; edits?: BookEdits }) {
  const [tip, setTip] = useState(false);
  const editing = useEditing();
  const current = READ_TYPES.find((t) => t.name === book.readType?.name);
  // Your read types by name (Settings › Read types); the starter four while they aren't loaded.
  const names = book.readTypes.length ? book.readTypes.map((t) => t.name) : READ_TYPES.map((t) => t.name);
  const keyOf = (name: string) => READ_TYPES.find((t) => t.name.toLowerCase() === name.toLowerCase())?.key ?? 'study';
  const tipOf = (name: string) => book.readTypes.find((t) => t.name === name)?.description ?? READ_TYPES.find((t) => t.name === name)?.tip;
  return (
    <div class="bk-plan">
      <div class="bk-plan-row">
        <Label>Read type</Label>
        {names.map((name) => {
          const on = book.readType?.name.toLowerCase() === name.toLowerCase();
          return (
            <button
              key={name}
              type="button"
              class={`bk-rt${on ? ' on' : ''}`}
              aria-pressed={on}
              title={`${tipOf(name) ?? name}${on ? ' · tap again to clear' : ''}`}
              disabled={!edits}
              onClick={() => void edits?.set('read_type', on ? null : name)}
            >
              <span class={`bk-rt-dot ${keyOf(name)}`} aria-hidden="true" />
              {name}
            </button>
          );
        })}
        <button type="button" class="bk-info" aria-label="What the read types mean" aria-expanded={tip} onClick={() => setTip(!tip)}>
          i
        </button>
      </div>
      {tip ? (
        <div class="bk-tip">
          {current ? `${current.name}: ${book.readType?.description ?? current.tip}.` : 'No read type.'}
        </div>
      ) : null}
      {editing && edits ? (
        <div class="bk-plan-row bk-plan-edit">
          <Label>Genre · tags</Label>
          <GenresTagsEdit book={book} edits={edits} />
        </div>
      ) : (
      <div class="bk-plan-row">
        <Label>Genre · tags</Label>
        {book.genres.length || book.subgenres.length ? (
          <span class="bk-tag genre">{[...book.genres, ...book.subgenres].join(' · ')}</span>
        ) : null}
        {book.tags.map((t) => (
          <span key={t} class="bk-tag">
            {t}
          </span>
        ))}
        {!book.genres.length && !book.subgenres.length && !book.tags.length ? <span class="bk-plan-quiet">None yet</span> : null}
      </div>
      )}
      <div class="bk-plan-row">
        <Label>Interest</Label>
        {edits ? (
          <InterestDots value={book.interest} hints={INTEREST_HINTS} onChange={(v) => void edits.set('interest', v)} />
        ) : book.interest !== null ? (
          <InterestDots value={book.interest} />
        ) : null}
        <span class="bk-plan-quiet">{interestHint(book.interest)}</span>
      </div>
    </div>
  );
}

/**
 * Your own words, set in place (Part 2): ✎ opens the field with Save · Cancel; Undo after
 * saving. Never shared. Edit mode covers the rest.
 */
function InPlace({
  label,
  field,
  value,
  edits,
  long,
  shown,
  empty,
}: {
  label: string;
  field: SetField;
  value: string | null;
  edits?: BookEdits;
  long?: boolean;
  shown: (v: string) => ComponentChildren;
  empty: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (draft !== null) {
    const save = async () => {
      setBusy(true);
      const ok = await edits?.set(field, draft);
      setBusy(false);
      if (ok) setDraft(null);
    };
    return (
      <div class="bk-inplace editing">
        {long ? (
          <textarea aria-label={label} value={draft} rows={4} maxLength={field === 'notes' ? 20000 : 5000} onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)} autoFocus />
        ) : (
          <input type="text" aria-label={label} value={draft} maxLength={1000} onInput={(e) => setDraft((e.target as HTMLInputElement).value)} autoFocus />
        )}
        <span class="bk-buttons">
          <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
            Save
          </button>
          <button type="button" class="btn-secondary" onClick={() => setDraft(null)}>
            Cancel
          </button>
          <span class="bk-quiet">Only you see this.</span>
        </span>
      </div>
    );
  }
  return (
    <span class="bk-inplace">
      {value ? shown(value) : <span class="bk-quiet">{empty}</span>}
      {edits ? (
        <button type="button" class="bk-pencil" aria-label={`Edit ${label}`} title={`Edit ${label}`} onClick={() => setDraft(value ?? '')}>
          ✎
        </button>
      ) : null}
    </span>
  );
}

function WhyRead({ book, edits }: { book: BookData; edits?: BookEdits }) {
  if (!book.whyRead && !edits) return null;
  return (
    <div class="bk-whyread">
      <Label>Why I want to read it</Label>
      <InPlace
        label="Why I want to read it"
        field="why_read"
        value={book.whyRead}
        edits={edits}
        long
        empty="Not written yet"
        shown={(v) => <p class="bk-why">“{v}”</p>}
      />
    </div>
  );
}

function OthersSay({ book, edits }: { book: BookData; edits?: BookEdits }) {
  const w = book.work;
  const gr =
    w.goodreads_score !== null
      ? `${goodreadsLine(w.goodreads_score, w.goodreads_ratings_count)}${w.goodreads_checked_on ? ` · checked ${dayText(w.goodreads_checked_on)}` : ''}`
      : null;
  return (
    <Fold id="others" title="What others say" hint={gr ? `Goodreads ${gr.split(' · ')[0]}` : undefined} open={true}>
      <Rows
        rows={[
          ['Goodreads', gr ?? <span class="bk-quiet">No score yet</span>],
          [
            'Recommended by',
            <InPlace label="Recommended by" field="recommended_by" value={book.recommendedBy} edits={edits} empty="Not recorded" shown={(v) => <span>{v}</span>} />,
          ],
          ['Awards & lists', book.honours?.length ? <Honours book={book} /> : null],
          ['My own lists & clubs', book.myLists.length ? book.myLists.join(' · ') : null],
        ]}
      />
    </Fold>
  );
}

/**
 * Awards & lists (n127, Kristi Nov 3): every catalog list the book's work is on, newest first, one
 * line each; with Discover on, each line opens that list's year. Read from the catalog, so it fills
 * in as lists are loaded, with nothing to type.
 */
function Honours({ book }: { book: BookData }) {
  const on = useFlag('discover');
  const { path, state } = useRoute();
  return (
    <ul class="bk-honours">
      {(book.honours ?? []).map((h, i) => (
        <li key={i}>
          {on ? (
            <Link href={`/discover/lists/${h.listId}/${h.year}`} state={trailState(path, state, book.work.title)}>
              {honourLine(h)}
            </Link>
          ) : (
            honourLine(h)
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * For fans of, under What others say (Kristi, Oct 27): hidden in view mode when blank; in edit
 * mode always shown, with ✎ (a shared fact: the admin, for now).
 */
function FansOf({ book, edits }: { book: BookData; edits?: BookEdits }) {
  const editing = useEditing();
  if (editing && edits) {
    return (
      <Fold id="fans" title="For fans of" open={true}>
        <FansOfEdit book={book} edits={edits} />
      </Fold>
    );
  }
  if (!book.work.for_fans_of) return null;
  return (
    <Fold id="fans" title="For fans of" open={true}>
      <p class="bk-fans">{book.work.for_fans_of}</p>
    </Fold>
  );
}

function Summary({ book }: { book: BookData }) {
  if (!book.work.summary) return null;
  const source = summarySource(book.work.summary_source);
  return (
    <Fold id="summary" title="Summary" open={true}>
      <Rows
        rows={[
          [
            'About',
            <span class="bk-summary">
              {book.work.summary}
              {source ? <span class="bk-source">{source}</span> : null}
            </span>,
          ],
        ]}
      />
    </Fold>
  );
}

function About({ book }: { book: BookData }) {
  const w = book.work;
  const pages: string[] = [];
  const lengths: string[] = [];
  for (const c of copiesInOrder(book)) {
    const e = editionOf(book, c);
    const name = copyName(book, c).split(' · ')[0]!;
    const p = c.pages ?? e?.pages;
    const m = c.listening_minutes ?? e?.listening_minutes;
    if (p && !pages.some((x) => x.startsWith(`${p} `))) pages.push(`${p} (${name})`);
    if (m && !lengths.some((x) => x.startsWith(timeText(m)))) lengths.push(`${timeText(m)} (${name})`);
  }
  const published = fuzzyText(w.first_published_on, w.first_published_precision, w.first_published_words);
  const audience = w.audience === 'young_adult' ? 'Young adult' : w.audience === 'children' ? 'Children' : null;
  const hint = [published, pages[0]?.split(' (')[0] ? `${pages[0]!.split(' (')[0]} pages` : null, bookLabel(book.label)].filter(Boolean).join(' · ');
  return (
    <Fold id="about" title="Book details" hint={hint} open={false}>
      <Rows
        rows={[
          ['Published', published || null],
          ['Pages', pages.join(' · ') || null],
          ['Listening', lengths.join(' · ') || null],
          ['For', audience],
          ['Book ID', bookLabel(book.label)],
        ]}
      />
    </Fold>
  );
}

function LibraryHistory({ book, copy, today, edits }: { book: BookData; copy: Copy; today: string; edits?: BookEdits }) {
  const myBooksOn = useFlag('my_books');
  const editing = useEditing();
  const [open, setOpen] = useRemembered('book.fold.library-history', false);
  const history = libraryHistory(book, today, copy.id);
  if (!history) return null;
  return (
    <div class="bk-lhist">
      <button type="button" class="bk-lhist-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span class="bk-arrow" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <Label>Library history</Label>
        <span class="bk-lhist-sum">{history.summary}</span>
        <span class="bk-lhist-verb">{open ? 'Hide' : `Show all ${history.steps.length} steps`}</span>
      </button>
      {open ? (
        <>
          <ol class="bk-log lib">
            {history.steps.map((s, i) => (
              <li key={i}>
                <span class="bk-log-date">{s.on ? dayText(s.on, today) : 'Date not recorded'}</span>
                <span>
                  <span class={`bk-kind ${s.tone}`}>{s.name}</span>
                </span>
                <span class="bk-log-text">
                  {s.detail}
                  {editing && edits && s.event ? <EventEdits event={s.event} edits={edits} /> : null}
                </span>
              </li>
            ))}
          </ol>
          {/* A link says what you'll do there, then where (check-up, Kristi Nov 2). */}
          {myBooksOn ? (
            <span class="bk-foot">
              <Link href="/my-books/library" class="btn-link">
                See every library loan in Library copies ›
              </Link>
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function CopyCard({ book, copy, today, edits }: { book: BookData; copy: Copy; today: string; edits?: BookEdits }) {
  const libOn = useFlag('book_page.library') && Boolean(edits);
  // Where ▾ and Lend it… on a paper copy come with My books › Owned copies (Part 3, 0046).
  const myBooksOn = useFlag('my_books');
  const openSheet = useSheet();
  const editing = useEditing();
  const e = editionOf(book, copy);
  const read = currentRead(book);
  const onRead = copiesInUse(book, read).some((c) => c.id === copy.id);
  const onOpenRead = onRead && read?.outcome === 'open';
  const usedBy = readsInOrder(book.reads)
    .map((r, i) => ({ r, n: i + 1 }))
    .filter(({ r }) => r.copies.some((c) => c.copy_id === copy.id));
  const library = copy.how === 'library_loan';
  const loan = library ? currentLoan(copy) : undefined;
  const pct = onOpenRead ? latestProgress(read) : null;
  const at = pct !== null ? positionOn(book, copy, pct) : null;
  const acquired = fuzzyText(copy.acquired_on, copy.acquired_precision, copy.acquired_words);
  const verb = copy.how === 'shared' ? 'Shared' : copy.how === 'free' ? 'Got it' : copy.how === 'gift' ? 'A gift' : 'Bought';
  // A gift: "A gift · Oct 2024 · from Mom"; free: "Got it Oct 2024 · from Sellwood branch free bin" (0047).
  const came = copy.how === 'gift' ? [verb, acquired] : [acquired ? `${verb} ${acquired}` : null];
  const from = copy.store ?? (copy.how === 'gift' || copy.how === 'free' ? (copy.got_from ?? null) : null);
  const stateWord = { have_it: null, lent_out: 'Lent out', lost: 'Lost', donated: 'Donated', returned: 'Returned' }[copy.state];
  const open = openTargets(book, isPhoneDevice()).find((t) => t.copyId === copy.id);
  const where = at ? `At ${Math.round(pct!)}%: ${at}` : !onRead && usedBy.length ? `Not on the current read · used for ${usedBy.map((u) => `Read ${u.n}`).join(', ')}` : null;

  if (library) {
    const badge = loan ? loanBadge(loan, onOpenRead) : null;
    const renew = loan ? renewFrom(loan) : null;
    const reading = badge === 'Reading';
    return (
      <article class={`bk-copy library${loan?.step === 'borrowed' ? ' now' : ''}`} aria-label={copyName(book, copy)}>
        {/* A library bar always carries its words (check-up, Kristi Nov 2), as on Shelves' cards. */}
        {loan?.step === 'borrowed' ? (
          <span class="library-label" aria-hidden="true">
            <span>Library copy</span>
          </span>
        ) : null}
        <div class="bk-copy-top">
          <h4 class="bk-copy-name">{copyName(book, copy)}</h4>
          {loan?.library ? <span class="bk-copy-sub">{loan.library} · Libby</span> : null}
          {badge ? <span class={`bk-badge${reading ? ' on' : ''}`}>{badge}</span> : null}
        </div>
        {loan ? (
          <span class="bk-copy-step">
            <span class={`bk-step${loan.step === 'borrowed' ? '' : ' quiet'}`}>{loanLine(loan, today, book.status === 'currently_reading')}</span>
          </span>
        ) : null}
        <CopyFactLines book={book} copy={copy} />
        {where ? <span class="bk-copy-line">{where}</span> : null}
        {renew && loan?.step === 'borrowed' ? (
          today >= renew ? (
            libOn ? null : <span class="bk-copy-line">
              <a class="btn-book" href="https://libbyapp.com/shelf/loans" target="_blank" rel="noopener noreferrer">
                Renew in Libby ↗
              </a>
            </span>
          ) : (
            <span class="bk-copy-line quiet">Renew in Libby ↗ appears from {dayText(renew, today)}, 3 days before it’s due.</span>
          )
        ) : null}
        {loan?.step === 'borrowed' && open ? (
          <div class="bk-buttons">
            <a class="btn-book" href={open.url} target="_blank" rel="noopener noreferrer">
              {open.label}
            </a>
          </div>
        ) : null}
        {libOn && edits ? (
          <LoanSteps
            book={book}
            copy={copy}
            edits={edits}
            onFinished={() => openSheet({ kind: 'finish' })}
            onDidntFinish={() => openSheet({ kind: 'didnt_finish', copy: copy.id })}
            onStart={() => openSheet({ kind: 'start' })}
          />
        ) : null}
        {copy.note && !editing ? <span class="bk-note">{copy.note}</span> : null}
        {editing && edits && loan ? <LoanEdits loan={loan} edits={edits} /> : null}
        {editing && edits ? <CopyEdits book={book} copy={copy} edits={edits} /> : null}
        <LibraryHistory book={book} copy={copy} today={today} edits={edits} />
      </article>
    );
  }
  return (
    <article class="bk-copy" aria-label={copyName(book, copy)}>
      <div class="bk-copy-top">
        <h4 class="bk-copy-name">{copyName(book, copy)}</h4>
        <span class="bk-copy-sub">
          {[...came, from ? `from ${from}` : null].filter(Boolean).join(' · ')}
          {e?.narrators.length ? (
            <>
              {[...came, from].some(Boolean) ? ' · ' : ''}narrated by {e.narrators.join(', ')} <span class="role-tag">Narrator</span>
            </>
          ) : null}
        </span>
        {open && copy.state === 'have_it' ? (
          <a class="btn-book bk-copy-open" href={open.url} target="_blank" rel="noopener noreferrer">
            {open.label}
          </a>
        ) : null}
      </div>
      <CopyFactLines book={book} copy={copy} />
      {copy.state === 'lent_out' && copy.lent_to ? (
        <span class="bk-step">
          Lent to {copy.lent_to}
          {copy.lent_on ? ` · since ${dayText(copy.lent_on, today)}` : ''}
        </span>
      ) : stateWord ? (
        <span class="bk-step">{stateWord}</span>
      ) : null}
      {(copy.room || copy.shelf) && copy.state !== 'lost' && copy.state !== 'donated' ? <span class="bk-copy-line">{[copy.room, copy.shelf].filter(Boolean).join(' · ')}</span> : null}
      {where ? <span class="bk-copy-line">{where}</span> : null}
      {copy.note && !editing ? <span class="bk-note">{copy.note}</span> : null}
      {myBooksOn && edits && !editing ? <CopyTools copy={copy} title={book.work.title} /> : null}
      {editing && edits ? <CopyEdits book={book} copy={copy} edits={edits} /> : null}
    </article>
  );
}

/** The facts line (the published edition) and, when set, your copy's own line (0039). */
function CopyFactLines({ book, copy }: { book: BookData; copy: Copy }) {
  const facts = editionFacts(book, copy);
  const mine = copyFacts(copy);
  return (
    <>
      {facts ? <span class="bk-copy-facts">{facts}</span> : null}
      {mine ? <span class="bk-copy-facts mine">{mine}</span> : null}
    </>
  );
}

/** + Add a copy…: Secondary (adding), in the side pane and at the foot of Copies & formats; hidden while book_page.library is off. */
function AddCopyButton({ edits, side }: { edits?: BookEdits; side?: boolean }) {
  const on = useFlag('book_page.library') && Boolean(edits);
  const openSheet = useSheet();
  return on ? (
    <button type="button" class={`btn-secondary${side ? ' bk-side-add' : ''}`} onClick={() => openSheet({ kind: 'add_copy' })}>
      + Add a copy…
    </button>
  ) : null;
}

function Copies({ book, today, edits }: { book: BookData; today: string; edits?: BookEdits }) {
  const copies = copiesInOrder(book);
  const history = libraryHistory(book, today);
  return (
    <Fold id="copies" title="Copies & formats" hint={`${copies.length} ${copies.length === 1 ? 'copy' : 'copies'}${history ? ` · ${history.summary}` : ''}`} open={true}>
      {copies.length ? (
        <div class="bk-copies">
          {copies.map((c) => (
            <CopyCard key={c.id} book={book} copy={c} today={today} edits={edits} />
          ))}
        </div>
      ) : (
        <p class="bk-quiet">No copies yet.</p>
      )}
      <div>
        <AddCopyButton edits={edits} />
      </div>
    </Fold>
  );
}

/** Reading as on the open read: Read · Listen · Read & Listen, or Use my copies (Part 3). */
function ReadingAsChoice({ book, read, edits }: { book: BookData; read: Read; edits: BookEdits }) {
  const as = readingAs(book, read);
  const opts: { v: 'read' | 'listen' | 'both'; label: string }[] = [
    { v: 'read', label: 'Read' },
    { v: 'listen', label: 'Listen' },
    { v: 'both', label: 'Read & Listen' },
  ];
  return (
    <div class="bk-chips" role="group" aria-label="Reading as">
      <Label>Reading as</Label>
      {opts.map((o) => {
        const on = as?.as === o.label;
        return (
          <Chip key={o.v} small label={o.label} on={on} onToggle={() => void edits.act('read_set_as', { reading: on && as?.set ? null : o.v }, `Reading as: ${o.label}`)} />
        );
      })}
      <span class="bk-quiet">{as?.set ? 'set by you' : 'from your copies'}</span>
      {as?.set ? (
        <button type="button" class="btn-link" onClick={() => void edits.act('read_set_as', { reading: null }, 'Reading as: from your copies')}>
          Use my copies
        </button>
      ) : null}
    </div>
  );
}

/** + Also using ▾: another of your copies on the open read (Part 3); a Secondary pill menu (R4). */
function AlsoUsing({ book, read, edits }: { book: BookData; read: Read; edits: BookEdits }) {
  const inUse = new Set(copiesInUse(book, read).map((c) => c.id));
  const others = copiesInOrder(book).filter((c) => !inUse.has(c.id) && c.state === 'have_it');
  if (!others.length) return null;
  return (
    <PillMenu
      label="+ Also using"
      ariaLabel="+ Also using"
      sheetTitle="Also using"
      choices={others.map((c) => ({ value: c.id, label: copyName(book, c) }))}
      onPick={(id) => {
        const c = others.find((x) => x.id === id)!;
        void edits.act('read_copy_add', { copy: c.id }, `Also using ${copyName(book, c)}`);
      }}
    />
  );
}

function Reads({ book, edits }: { book: BookData; edits?: BookEdits }) {
  const ordered = readsInOrder(book.reads);
  const openSheet = useSheet();
  const editing = useEditing();
  if (!ordered.length && !editing) return null;
  const numbered = ordered.map((r, i) => ({ r, n: i + 1 })).reverse();
  return (
    <Fold id="reads" title="My reads" hint={`${ordered.length} ${ordered.length === 1 ? 'read' : 'reads'}`} open={true}>
      <ol class="bk-reads">
        {numbered.map(({ r, n }) => {
          const as = readingAs(book, r);
          const copies = book.copies.filter((c) => r.copies.some((rc) => rc.copy_id === c.id));
          return (
            <li key={r.id} class={`bk-read${r.outcome === 'open' ? ' now' : ''}`}>
              <div class="bk-read-top">
                <span class="bk-read-name">Read {n}</span>
                <span class="bk-read-when">{readWhen(r)}</span>
                <span class="bk-read-state">{readState(r)}</span>
              </div>
              {copies.length ? (
                <div class="bk-chips">
                  <Label>Copies</Label>
                  {copies.map((c) => {
                    const stopped = r.copies.find((rc) => rc.copy_id === c.id)?.stopped_using_on;
                    const canStop = edits && r.outcome === 'open' && !stopped && copiesInUse(book, r).length > 1;
                    return (
                      <span key={c.id} class={`bk-chip${stopped ? ' stopped' : ''}`}>
                        {copyName(book, c)}
                        {stopped ? ` · stopped using ${dayText(stopped)}` : ''}
                        {canStop ? (
                          <button
                            type="button"
                            class="bk-chip-x"
                            aria-label={`Stopped using ${copyName(book, c)}`}
                            title="Stopped using this"
                            onClick={() => void edits.act('read_copy_stop', { copy: c.id }, `Stopped using ${copyName(book, c)}`)}
                          >
                            ✕
                          </button>
                        ) : null}
                      </span>
                    );
                  })}
                  {edits && r.outcome === 'open' ? <AlsoUsing book={book} read={r} edits={edits} /> : null}
                </div>
              ) : null}
              {edits && r.outcome === 'open' ? (
                <ReadingAsChoice book={book} read={r} edits={edits} />
              ) : as ? (
                <span class="bk-read-as">
                  Reading as: {as.as} · {as.set ? 'set by you' : 'from your copies'}
                </span>
              ) : null}
              {edits && r.outcome === 'finished' ? (
                <Stars
                  value={r.rating}
                  label={`Stars for Read ${n}`}
                  onChange={(v) => void edits.act('read_rate', { read_id: r.id, rating: v }, v === null ? 'Stars cleared' : `Rated ★${v}`)}
                />
              ) : null}
              {r.outcome === 'stopped' && (r.stop_reason || r.stop_chips.length) ? (
                <span class="bk-note">{[...r.stop_chips, r.stop_reason].filter(Boolean).join(' · ')}</span>
              ) : null}
              {editing && edits ? (
                <ReadEdits book={book} read={r} n={n} edits={edits} />
              ) : r.takeaways.length ? (
                <div class="bk-takeaways">
                  <Label>Key takeaways</Label>
                  <ul>
                    {r.takeaways.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
      {editing && edits ? (
        <div>
          <button type="button" class="btn-secondary" onClick={() => openSheet({ kind: 'past' })}>
            + Add a past read
          </button>
        </div>
      ) : null}
      {/* A re-read only after a finished read; a book only stopped starts again (Kristi, Oct 27). */}
      {!openRead(book) && ordered.length && edits ? (
        <div>
          <button type="button" class="btn-book" onClick={() => openSheet({ kind: 'start' })}>
            {ordered.some((r) => r.outcome === 'finished') ? 'Start a re-read…' : 'Start again…'}
          </button>
        </div>
      ) : null}
    </Fold>
  );
}

/** Recent changes (Kristi, Oct 27): folds like the other sections, remembered on this device. */
function RecentFold({ book, edits }: { book: BookData; edits: BookEdits }) {
  const { actions, refresh } = useRecentChanges(book);
  if (!actions?.length) return null;
  const open = actions.filter((a) => !a.undone_at).length;
  return (
    <Fold id="recent" title="Recent changes" hint={`${actions.length} in the last day${open ? ` · ${open} can be undone` : ''}`} open={true}>
      <RecentChanges actions={actions} refresh={refresh} edits={edits} />
    </Fold>
  );
}

function MyHistory({ book, today }: { book: BookData; today: string }) {
  const ordered = readsInOrder(book.reads);
  const readNumber = (id: string) => {
    const i = ordered.findIndex((r) => r.id === id);
    return i < 0 ? null : i + 1;
  };
  const day = (iso: string) => dayText(todayIn('America/Los_Angeles', new Date(iso)), today);
  const steps = [...book.history]
    .sort((a, b) => (a.happened_at < b.happened_at ? 1 : -1))
    .map((h) => ({ id: h.id, on: day(h.happened_at), ...historyParts(h, readNumber) }));
  // The import writes one History row per batch, not per book (rule 2), so a book's first step
  // comes from when and how it was added.
  if (book.addedAt && !book.history.some((h) => h.kind === 'added' || h.kind === 'import_batch')) {
    const how = book.addedHow && ADDED_HOW[book.addedHow] ? ADDED_HOW[book.addedHow]! : '';
    steps.push({ id: 'added', on: day(book.addedAt), name: 'Added', detail: how });
  }
  if (!steps.length) return null;
  const latest = steps[0]!;
  return (
    <Fold
      id="history"
      title="My history"
      hint={`${steps.length} ${steps.length === 1 ? 'step' : 'steps'} · latest: ${[latest.name, latest.detail].filter(Boolean).join(' ')}, ${latest.on}`}
      open={false}
    >
      <ol class="bk-log">
        {steps.map((h) => (
          <li key={h.id}>
            <span class="bk-log-date">{h.on}</span>
            <span>
              <span class={`bk-kind ${h.name === 'Moved' ? 'grey' : h.name === 'Added' ? 'added' : 'teal'}`}>{h.name}</span>
            </span>
            <span class="bk-log-text">{h.detail}</span>
          </li>
        ))}
      </ol>
      <span class="bk-foot">Shelf moves, set-asides and passes for this book. Reads are in My reads above; loans in Copies & formats.</span>
    </Fold>
  );
}

function Relation({ book, today, edits }: { book: BookData; today: string; edits?: BookEdits }) {
  const open = openRead(book);
  const finished = readsInOrder(book.reads)
    .filter((r) => r.outcome === 'finished')
    .at(-1);
  const started = open ? fuzzyText(open.started_on, open.started_precision, open.started_words) : '';
  const forDays = open?.started_on && open.started_precision === 'day' ? daysBetween(open.started_on, today) : null;
  const added = book.addedAt ? `${dayText(todayIn('America/Los_Angeles', new Date(book.addedAt)), today)}${book.addedHow && ADDED_HOW[book.addedHow] ? ` · ${ADDED_HOW[book.addedHow]}` : ''}` : null;
  const history = libraryHistory(book, today);
  const counts = [
    book.readingNotes.length ? `${book.readingNotes.length} reading ${book.readingNotes.length === 1 ? 'note' : 'notes'}` : null,
    book.quotes.length ? `${book.quotes.length} ${book.quotes.length === 1 ? 'quote' : 'quotes'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Fold id="relation" title="My relation to the book" hint={started ? `started ${started}` : undefined} open={true}>
      <Rows
        rows={[
          ['Added', added],
          // Review's Save (Kristi, Oct 30: shown quietly here).
          ['Reviewed', book.lastReviewedAt ? dayText(todayIn('America/Los_Angeles', new Date(book.lastReviewedAt)), today) : null],
          ['Started', started ? `${started}${forDays !== null && forDays >= 0 ? ` · reading for ${forDays} ${forDays === 1 ? 'day' : 'days'}` : ''}` : null],
          ['Finished', finished ? readWhen(finished).replace(/^Finished /, '') : null],
          ['My rating', ratingLine(book.reads)],
          // ♥ has a dated line here, not a History row (Kristi, Oct 27).
          ['Favorite', book.favoriteAt ? `♥ since ${dayText(todayIn('America/Los_Angeles', new Date(book.favoriteAt)), today)}` : null],
          ['Library loans', history ? history.summary : null],
          ['Note about the book', book.notes || edits ? <InPlace label="Note about the book" field="notes" value={book.notes} edits={edits} long empty="None yet" shown={(v) => <span class="bk-notes">{v}</span>} /> : null],
          ['Notes & quotes', counts || null],
        ]}
      />
    </Fold>
  );
}

/** "Loc 1,204" → 1204, for book order. */
const locationNumber = (q: Quote) => {
  const m = /(\d[\d,]*)/.exec(q.location ?? '');
  return m ? Number(m[1]!.replace(/,/g, '')) : Number.POSITIVE_INFINITY;
};

function QuotesNotes({ book, today, edits }: { book: BookData; today: string; edits?: BookEdits }) {
  const pasteOn = useFlag('book_page.paste_kindle') && Boolean(edits);
  const [pasting, setPasting] = useState(false);
  const lastPasted = book.quotes.filter((q) => q.source === 'kindle').map((q) => q.created_at).sort().at(-1);
  const editing = useEditing();
  const [adding, setAdding] = useState<'note' | 'quote' | null>(null);
  const notes = [...book.readingNotes].sort((a, b) => ((a.noted_on ?? '') < (b.noted_on ?? '') ? 1 : -1));
  const quotes = [...book.quotes].sort((a, b) => locationNumber(a) - locationNumber(b) || (a.created_at < b.created_at ? -1 : 1));
  const linked = (q: Quote) => book.readingNotes.filter((n) => n.quote_id === q.id);
  const fromWords = (s: string) => (s === 'kindle' ? 'from Kindle' : s === 'import' ? 'from the sheet' : null);
  const noteOn = (n: Note) => {
    const q = n.quote_id ? book.quotes.find((x) => x.id === n.quote_id) : undefined;
    return [fromWords(n.source), q ? `on the quote at ${q.location ?? 'its place'}` : n.location, n.from_removed_read ? 'from a removed read' : null].filter(Boolean).join(' · ');
  };
  const dateOf = (n: Note) => (n.noted_on ? dayText(n.noted_on, today) : 'Undated');
  return (
    <Fold
      id="quotes"
      title="Quotes & reading notes"
      hint={[notes.length ? `${notes.length} reading notes` : null, quotes.length ? `${quotes.length} quotes` : null].filter(Boolean).join(' · ') || undefined}
      open={true}
    >
      <div class="bk-buttons">
        {edits ? (
          <>
            <button type="button" class="btn-secondary" aria-expanded={adding === 'note'} onClick={() => setAdding(adding === 'note' ? null : 'note')}>
              + Reading note
            </button>
            <button type="button" class="btn-secondary" aria-expanded={adding === 'quote'} onClick={() => setAdding(adding === 'quote' ? null : 'quote')}>
              + Add a quote
            </button>
          </>
        ) : null}
        {pasteOn ? (
          <button type="button" class="btn-book" onClick={() => setPasting(true)}>
            {lastPasted ? 'Paste from Kindle again…' : 'Paste from Kindle…'}
          </button>
        ) : null}
        {pasteOn && lastPasted ? (
          <span class="bk-quiet">Last pasted {dayText(todayIn('America/Los_Angeles', new Date(lastPasted)), today)} · pasting again adds only what’s new</span>
        ) : null}
      </div>
      {pasting && edits ? <PasteKindleSheet book={book} edits={edits} onClose={() => setPasting(false)} /> : null}
      {edits && adding ? <AddEntry kind={adding} book={book} edits={edits} onClose={() => setAdding(null)} /> : null}
      <div class="bk-qn">
        <div class="bk-qn-col">
          <h4 class="bk-qn-head">
            <Label aside="newest first">Reading notes · {notes.length}</Label>
          </h4>
          {notes.length ? (
            notes.map((n) => (
              <div key={n.id} class="bk-qn-item">
                <span class="bk-qn-note">
                  <strong>{dateOf(n)}</strong> · {n.body}
                </span>
                {noteOn(n) ? <span class="bk-qn-where">{noteOn(n)}</span> : null}
                {editing && edits ? <EntryEdits kind="note" entry={n} edits={edits} /> : null}
              </div>
            ))
          ) : (
            <div class="bk-qn-item bk-qn-empty">No reading notes yet.</div>
          )}
        </div>
        <div class="bk-qn-col">
          <h4 class="bk-qn-head">
            <Label aside="by place in the book">Quotes · {quotes.length}</Label>
          </h4>
          {quotes.length ? (
            quotes.map((q) => (
              <div key={q.id} class="bk-qn-item">
                <blockquote class="bk-quote">{q.body}</blockquote>
                <span class="bk-qn-where">{[q.location, q.chapter, fromWords(q.source), q.from_removed_read ? 'from a removed read' : null].filter(Boolean).join(' · ')}</span>
                {editing && edits ? <EntryEdits kind="quote" entry={q} edits={edits} /> : null}
                {linked(q).map((n) => (
                  <div key={n.id} class="bk-linked">
                    <span class="bk-kind teal">Reading note</span>
                    <span>
                      <strong>{dateOf(n)}</strong> · {n.body}
                    </span>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div class="bk-qn-item bk-qn-empty">No quotes yet. Add one, or paste your Kindle highlights.</div>
          )}
        </div>
      </div>
    </Fold>
  );
}

/**
 * A More like this tile, as in today's app (Kristi, Oct 28: quicker to read): the cover, the title
 * in bold, the author under it, the dashed Not owned yet pill when you have no copy of your own
 * (not on Reference books), then the shelf in a grey pill. Same series shows "Book 3" with the author.
 */
function MoreRow({ books, from }: { books: { label: number; title: string; cover: string | null; author?: string | null; pre?: string | null; owned?: boolean; status: Status | null }[]; from: { from: string; full?: boolean } }) {
  return (
    <ul class="bk-more">
      {books.map((s) => (
        <li key={s.label}>
          <Link href={`/book/${bookLabel(s.label)}`} state={from} class="bk-more-link">
            <Cover title={s.title} url={s.cover} size="sm" />
            <span class="bk-more-title">{s.title}</span>
          </Link>
          {s.author || s.pre ? <span class="bk-more-author">{[s.pre, s.author].filter(Boolean).join(' · ')}</span> : null}
          {s.owned === false && s.status !== 'reference' ? <span class="bk-more-notowned">Not owned yet</span> : null}
          <span class="bk-more-shelf">{statusName(s.status)}</span>
        </li>
      ))}
    </ul>
  );
}

function MoreLikeThis({ book }: { book: BookData }) {
  const { path, state } = useRoute();
  const byAuthor = book.sameAuthor ?? [];
  if (!book.sameSeries.length && !book.sameSubgenre.length && !byAuthor.length) return null;
  const from = { from: state.from ?? path, full: state.full };
  // Same series, then books sharing a subgenre (Kristi, Oct 26); similar books from Discover come later.
  return (
    <Fold id="more" title="More like this · on your list" open={true}>
      {book.sameSeries.length ? (
        <div class="bk-row-block">
          <Label>Same series</Label>
          <MoreRow
            from={from}
            books={book.sameSeries.map((s) => ({ ...s, pre: s.position !== null ? `Book ${s.position}` : null }))}
          />
        </div>
      ) : null}
      {byAuthor.length ? (
        <div class="bk-row-block">
          <Label>
            More by{' '}
            <NameLink kind="author" id={book.authorLinks?.[0]?.id} here={book.work.title}>
              {book.authors[0] ?? 'the author'}
            </NameLink>
          </Label>
          <MoreRow from={from} books={byAuthor} />
        </div>
      ) : null}
      {book.sameSubgenre.length ? (
        <div class="bk-row-block">
          <Label>Same subgenre</Label>
          <MoreRow from={from} books={book.sameSubgenre} />
        </div>
      ) : null}
    </Fold>
  );
}

/**
 * ↻ Read again once there's a finished read, ↺ Try again once there's a stopped read (Kristi,
 * Oct 27, later), or while either is on so it can come off. Each with Undo. ♥ Favorite is under
 * the reading actions (FavoriteButton).
 */
function Marks({ book, edits }: { book: BookData; edits?: BookEdits }) {
  const marks = [
    { mark: 'read_again' as const, on: Boolean(book.readAgainAt), label: '↻ Read again', tip: 'You’d like to read it again', cls: 'bk-mark' },
    { mark: 'try_again' as const, on: Boolean(book.tryAgainAt), label: '↺ Try again', tip: 'You mean to give it another go', cls: 'bk-mark' },
  ].filter(
    (m) =>
      m.on ||
      (m.mark === 'read_again' && book.reads.some((r) => r.outcome === 'finished')) ||
      (m.mark === 'try_again' && book.reads.some((r) => r.outcome === 'stopped')),
  );
  if (!marks.length) return null;
  return (
    <div class="bk-side-marks" role="group" aria-label="Marks">
      {marks.map((m) => (
        <button
          key={m.mark}
          type="button"
          class={`btn-book ${m.cls}${m.on ? ' on' : ''}`}
          aria-pressed={m.on}
          title={`${m.tip}${m.on ? ' · tap to take it off' : ''}`}
          disabled={!edits}
          onClick={() => void edits?.mark(m.mark, !m.on)}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

/** The whole page for one book. `wide`: two columns (laptop, iPad); otherwise one (phone). */
export function BookView({
  book,
  today = todayIn(),
  edits,
  editing = false,
  onRemoved,
}: {
  book: BookData;
  today?: string;
  edits?: BookEdits;
  /** Edit mode (Part 5): ✎ on every field. Edit details and the Editing bar live in the page's pinned bar (BookFrame). */
  editing?: boolean;
  /** After Remove this book: back to where the page was opened from. */
  onRemoved?: () => void;
}) {
  const cover = coverOf(book);
  const [sheet, setSheet] = useState<OpenSheet>(null);
  const close = () => setSheet(null);
  return (
    <SheetCtx.Provider value={setSheet}>
    <EditingCtx.Provider value={editing && Boolean(edits)}>
    <div class={`bk${editing ? ' editing' : ''}`}>
      <div class="bk-grid">
        <div class="bk-cover">
          <Cover title={book.work.title} url={cover} size="lg" />
        </div>
        <Head book={book} today={today} edits={edits} />
        <div class="bk-side">
          <OpenIn book={book} />
          <AddCopyButton edits={edits} side />
          <Progress book={book} edits={edits} />
          <SideLinks book={book} />
          <Marks book={book} edits={edits} />
        </div>
        <div class="bk-main">
          <ReadingActions book={book} edits={edits} />
          <Plan book={book} edits={edits} />
          <WhyRead book={book} edits={edits} />
          <FoldAll />
          {/* Summary right under Why I want to read it (Kristi, Oct 28); in edit mode it's edited in About the book. */}
          {editing && edits ? null : <Summary book={book} />}
          <OthersSay book={book} edits={edits} />
          <FansOf book={book} edits={edits} />
          {editing && edits ? <EditAbout book={book} edits={edits} /> : <About book={book} />}
          <Copies book={book} today={today} edits={edits} />
          <Reads book={book} edits={edits} />
          {/* Kristi, Oct 27: My relation and Quotes & reading notes under My reads, then Recent changes. */}
          <Relation book={book} today={today} edits={edits} />
          <QuotesNotes book={book} today={today} edits={edits} />
          {edits ? <RecentFold book={book} edits={edits} /> : null}
          <MyHistory book={book} today={today} />
          <MoreLikeThis book={book} />
          {editing && edits ? <RemoveBook book={book} edits={edits} onRemoved={() => onRemoved?.()} /> : null}
        </div>
      </div>
      {edits && sheet?.kind === 'start' ? <StartSheet book={book} edits={edits} onClose={close} /> : null}
      {edits && sheet?.kind === 'stop' ? <StopSheet book={book} edits={edits} onClose={close} initial={sheet.to} /> : null}
      {edits && sheet?.kind === 'finish' ? <FinishSheet book={book} edits={edits} onClose={close} /> : null}
      {edits && sheet?.kind === 'past' ? <PastReadSheet book={book} edits={edits} onClose={close} move={sheet.move} /> : null}
      {edits && sheet?.kind === 'add_copy' ? <AddCopySheet book={book} edits={edits} onClose={close} /> : null}
      {edits && sheet?.kind === 'didnt_finish' ? <StopSheet book={book} edits={edits} onClose={close} loanCopy={sheet.copy} /> : null}
      {edits && sheet?.kind === 'place' ? <MovePlaceSheet book={book} to={sheet.to} edits={edits} onClose={close} /> : null}
    </div>
    </EditingCtx.Provider>
    </SheetCtx.Provider>
  );
}
